import {
  Button,
  DatePicker,
  Dropdown,
  Icon,
  Input,
  Menu,
  Row,
  Table,
  notification,
} from "antd";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import MenuIcon from "../../../components/utils/table/icons/menu";
import { Container as MenuContainer } from "../../../components/utils/table/menu/styles";
import usePicketSupplementSupplyContext from "../../../hooks/usePicketSupplementSupplyContext";
import { numberMask } from "../../../services/helpersMethodsService";
import { destroyBySupplyDate } from "../../../services/picketSupplementSupplyService";
import { getColumnNumberWithBetweenSearchProps } from "../../../utils/tableUtils";
import { formatReadingScale } from "../../../utils/readingScaleUtils";

const FIELD_MAP = {
  supplyDate: "supply_date",
  picketsCount: "pickets_count",
  totalSupplementSupplied: "total_supplement_supplied",
  responsibleName: "responsible_name",
};

const OPERATOR_MAP = {
  equal: "=",
  greaterThan: ">",
  greaterOrEqualThan: ">=",
  lessThan: "<",
  lessOrEqualThan: "<=",
  between: "between",
};

function getSearchString(filters) {
  if (!filters || Object.keys(filters).length === 0) return "";
  const parts = [];
  Object.entries(filters).forEach(([k, v]) => {
    if (!v || (Array.isArray(v) && v.length === 0)) return;
    const field = FIELD_MAP[k] || k;
    if (k === "supplyDate" && v[0]?.dates) {
      const [start, end] = v[0].dates;
      if (start && end)
        parts.push(
          `${field} between '${start.format("YYYY-MM-DD")}' and '${end.format("YYYY-MM-DD")}'`
        );
    } else if (k === "picketsCount" || k === "totalSupplementSupplied") {
      const op = OPERATOR_MAP[v[1]] ?? "=";
      const fragment =
        op === "between"
          ? `${field} between ${v[0]} and ${v[2]}`
          : `${field} ${op} ${v[0]}`;
      parts.push(fragment);
    } else {
      const str = Array.isArray(v) ? v[0] : v;
      if (str) parts.push(`upper(${field}) like upper('%25${str}%25')`);
    }
  });
  return parts.join(";");
}

function getSortField(sorter) {
  if (!sorter?.columnKey) return null;
  return {
    field: FIELD_MAP[sorter.columnKey] || sorter.columnKey,
    order: sorter.order,
  };
}

function PicketSupplementSupplyTable() {
  const { dataList, isLoadingDataList, fetchData, refreshKey } =
    usePicketSupplementSupplyContext();
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const [tableState, setTableState] = useState({
    page: 1,
    size: 10,
    sorter: null,
    filters: {},
  });

  useEffect(() => {
    const search = getSearchString(tableState.filters);
    const sorter = getSortField(tableState.sorter);
    fetchData(tableState.page, tableState.size, sorter, search);
  }, [tableState.page, tableState.size, tableState.sorter, tableState.filters, refreshKey, fetchData]);

  const handleTableChange = useCallback((pagination, filters, sorter) => {
    setTableState({
      page: pagination.current,
      size: pagination.pageSize,
      sorter: sorter?.columnKey ? sorter : null,
      filters: filters || {},
    });
  }, []);

  function expandedRowsPicketsTreats(record, index, indent, expanded) {
    const {
      subTable: {
        supplyNumber,
        supplementName,
        dryMatterPercentage,
        quantitySupplied,
        responsibleName,
        version,
        prevSupplyHourInMinutes,
        supplyHourInMinutes,
        readFeederValue,
      },
    } = translation.picketSupplementSupply.table.columns.subTable;

    const columns = [
      {
        title: supplyNumber,
        dataIndex: "supplyNumber",
      },
      {
        title: supplementName,
        dataIndex: "supplementName",
      },
      {
        title: dryMatterPercentage,
        dataIndex: "dryMatterPercentage",
        render: (text) => `${numberMask(text, false)}%`,
      },
      {
        title: quantitySupplied,
        dataIndex: "quantitySupplied",
        render: (text) => `${numberMask(text, false)} Kg`,
      },
      {
        title: prevSupplyHourInMinutes,
        dataIndex: "prevSupplyHourInMinutes",
        render: (text) => {
          if (!text) return "";
          const hour = Math.floor(Number(text / 60));
          const minutes = Number(text % 60);
          return `${hour.toLocaleString("pt-BR", {
            minimumIntegerDigits: 2,
            minimumFractionDigits: 0,
            useGrouping: false,
          })}:${minutes.toLocaleString("pt-BR", {
            minimumIntegerDigits: 2,
            minimumFractionDigits: 0,
            useGrouping: false,
          })}`;
        },
      },
      {
        title: supplyHourInMinutes,
        dataIndex: "supplyHourInMinutes",
        render: (text) => {
          if (!text) return "";
          const hour = Math.floor(Number(text / 60));
          const minutes = Number(text) % 60;
          return `${hour.toLocaleString("pt-BR", {
            minimumIntegerDigits: 2,
            minimumFractionDigits: 0,
            useGrouping: false,
          })}:${minutes.toLocaleString("pt-BR", {
            minimumIntegerDigits: 2,
            minimumFractionDigits: 0,
            useGrouping: false,
          })}`;
        },
      },
      {
        title: responsibleName,
        dataIndex: "responsibleName",
      },
      {
        title: readFeederValue,
        dataIndex: "readFeederValue",
        render: (text) => text != null && formatReadingScale(text),
      },
      {
        title: version,
        dataIndex: "version",
        render: (text) => `${text}°`,
      },
    ];

    const { treatments } = record;

    if (!treatments || treatments.length === 0) return false;

    return (
      <Table
        rowKey="id"
        columns={columns}
        dataSource={treatments}
        pagination={false}
        style={{ padding: "2" }}
      />
    );
  }

  function expandedRowsPickets(record, index, indent, expanded) {
    const {
      subTable: {
        retreatName,
        picketName,
        lotName,
        totalAnimals,
        supplementName,
        weightedAverageDryMatter,
        totalSupplementSupplied,
        productionSubSystem,
      },
    } = translation.picketSupplementSupply.table.columns;

    const columns = [
      {
        title: retreatName,
        dataIndex: "retreatName",
      },
      {
        title: picketName,
        dataIndex: "picketName",
      },
      {
        title: lotName,
        dataIndex: "lotName",
      },
      {
        title: totalAnimals,
        dataIndex: "totalAnimals",
      },
      {
        title: supplementName,
        dataIndex: "supplementName",
      },
      {
        title: weightedAverageDryMatter,
        dataIndex: "weightedAverageDryMatter",
        render: (text) => `${numberMask(text * 100, false)}%`,
      },
      {
        title: totalSupplementSupplied,
        dataIndex: "totalSupplementSupplied",
        render: (text) => `${numberMask(text, false)} Kg`,
      },
      {
        title: productionSubSystem,
        dataIndex: "productionSubSystem",
        render: (text) => translation.productionSubSystem[text],
      },
    ];

    const { pickets } = record;

    if (!pickets || pickets.length === 0) return false;

    return (
      <Table
        rowKey="picketId"
        columns={columns}
        dataSource={pickets}
        pagination={false}
        style={{ padding: "2" }}
        expandedRowRender={expandedRowsPicketsTreats}
      />
    );
  }

  const handleSearch = useCallback((selectedKeys, confirm) => {
    confirm();
  }, []);

  const handleReset = useCallback((clearFilters) => {
    clearFilters();
  }, []);

  const handleGetDateSearchProps = () => {
    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div
          style={{
            padding: 8,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Row style={{ marginBottom: 5 }}>
            <DatePicker.RangePicker
              value={selectedKeys && selectedKeys[0]?.dates}
              format="DD/MM/YYYY"
              onChange={(dates) => setSelectedKeys([{ dates }])}
            />
          </Row>
          <Row>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm)}
              icon="search"
              size="small"
              style={{
                width: 90,
                marginRight: 8,
                background: "#684e94",
                borderColor: "none",
                border: "none",
              }}
            />
            <Button
              onClick={() => handleReset(clearFilters)}
              icon="delete"
              size="small"
              style={{ width: 90 }}
            />
          </Row>
        </div>
      ),
      filterIcon: (filtered) => (
        <Icon
          type="calendar"
          style={{ color: filtered ? "#684e94" : undefined }}
        />
      ),
    };
  };

  const handleGetColumnSearchProps = useCallback(
    (dataIndex) => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon="search"
            size="small"
            style={{
              width: 90,
              marginRight: 8,
              background: "#684e94",
              borderColor: "none",
              border: "none",
            }}
          />
          <Button
            onClick={() => handleReset(clearFilters)}
            icon="delete"
            size="small"
            style={{ width: 90 }}
          />
        </div>
      ),
      filterIcon: (filtered) => (
        <Icon
          type="search"
          style={{ color: filtered ? "#684e94" : undefined }}
        />
      ),
    }),
    [handleReset, handleSearch]
  );

  const handleDelete = useCallback(
    async (supplyDate) => {
      try {
        await destroyBySupplyDate({
          groupId,
          farmId,
          supplyDate,
        });
        const search = getSearchString(tableState.filters);
        const sorter = getSortField(tableState.sorter);
        fetchData(tableState.page, tableState.size, sorter, search);
        notification.success({
          message: "O último trato foi deletado com sucesso!",
        });
      } catch (error) {
        notification.error({
          message: "Erro",
          description: "Erro ao deletar último trato.",
        });
      }
    },
    [farmId, fetchData, groupId, tableState]
  );

  const menu = (id, record) => {
    return (
      <Menu>
        <Menu.Item onClick={() => handleDelete(record.supplyDate)}>
          {translation.table.menu.delete}
        </Menu.Item>
      </Menu>
    );
  };

  return (
    <Table
      dataSource={dataList.content}
      loading={isLoadingDataList}
      rowKey={"supplyDate"}
      expandedRowRender={expandedRowsPickets}
      onChange={handleTableChange}
      pagination={{
        total: dataList.totalElements,
        current: tableState.page,
        pageSize: tableState.size,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "100"],
        hideOnSinglePage: dataList.totalElements <= 10,
      }}
    >
      <Table.Column
        key="supplyDate"
        dataIndex="supplyDate"
        title={translation.picketSupplementSupply.table.columns.supplyDate}
        sorter={true}
        sortOrder={
          tableState.sorter?.columnKey === "supplyDate"
            ? tableState.sorter.order
            : null
        }
        {...handleGetDateSearchProps()}
        render={(text) => moment(text).format("DD/MM/YYYY")}
      />
      <Table.Column
        key="picketsCount"
        dataIndex="picketsCount"
        title={translation.picketSupplementSupply.table.columns.picketsCount}
        sorter={true}
        sortOrder={
          tableState.sorter?.columnKey === "picketsCount"
            ? tableState.sorter.order
            : null
        }
        {...getColumnNumberWithBetweenSearchProps("picketsCount", handleReset, handleSearch)}
      />
      <Table.Column
        key="totalSupplementSupplied"
        dataIndex="totalSupplementSupplied"
        title={
          translation.picketSupplementSupply.table.columns
            .totalSupplementSupplied
        }
        sorter={true}
        sortOrder={
          tableState.sorter?.columnKey === "totalSupplementSupplied"
            ? tableState.sorter.order
            : null
        }
        {...getColumnNumberWithBetweenSearchProps("totalSupplementSupplied", handleReset, handleSearch)}
        render={(text) => `${numberMask(text, false)} kg`}
      />
      <Table.Column
        key="responsibleName"
        dataIndex="responsibleName"
        title={translation.picketSupplementSupply.table.columns.responsibleName}
        sorter={true}
        sortOrder={
          tableState.sorter?.columnKey === "responsibleName"
            ? tableState.sorter.order
            : null
        }
        {...handleGetColumnSearchProps("responsibleName")}
      />
      <Table.Column
        align="left"
        width={50}
        render={(text, record) => (
          <Dropdown
            overlay={menu(record.id, record)}
            trigger={["click"]}
            key={record.id}
          >
            <MenuContainer>
              <MenuIcon />
            </MenuContainer>
          </Dropdown>
        )}
      />
    </Table>
  );
}

export default PicketSupplementSupplyTable;
